/**
 * Permission Configuration - Single source of truth
 * Maps permissions to routes, sidebar items, and categories
 */

// Normalize string for flexible matching
export const normalize = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .trim()
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .replace(/&/g, "and");
};

// Permission to Route mapping
export const PERMISSION_ROUTE_MAP = {
  // MAIN - Standalone
  dashboard: "/admin/dashboard",
  transactions: "/admin/transactions",
  customers: "/admin/customers",

  // MAIN - Orders & Bookings
  "bulk orders": "/admin/bulk-orders",
  "catering orders": "/admin/catering-orders",
  "archived orders": "/admin/archived-orders",

  // MANAGEMENT - Company Management
  "company setup": "/admin/company-setup",
  staff: "/admin/staff",
  revenue: "/admin/revenue",
  "order and operations": "/admin/operations",

  // MANAGEMENT - Product Management
  category: "/admin/categories",
  "bulk purchase": "/admin/bulk-purchases",
  catering: "/admin/catering",
  "inventory control": "/admin/inventory-control",

  // MANAGEMENT - Customer Experience
  "promos and discounts": "/admin/promotions",
  "promos discounts": "/admin/promotions", // Alternative format from backend
  "feedback and ratings": "/admin/feedback",
  "feedback ratings": "/admin/feedback", // Alternative format
  "terms and conditions": "/admin/terms&conditions",
};

// Sidebar structure definition
export const SIDEBAR_STRUCTURE = {
  MAIN: {
    order: 1,
    label: "MAIN",
    modules: {
      Dashboard: {
        order: 1,
        standalone: true,
        path: "/admin/dashboard",
        icon: "DashboardOutlined",
        permissions: ["dashboard"],
      },
      "Orders & Bookings": {
        order: 2,
        standalone: false,
        icon: "ShoppingCartOutlined",
        permissions: ["bulk orders", "catering orders", "archived orders"],
        children: [
          {
            label: "Bulk Orders",
            path: "/admin/bulk-orders",
            permission: "bulk orders",
          },
          {
            label: "Catering Orders",
            path: "/admin/catering-orders",
            permission: "catering orders",
          },
          {
            label: "Archived Orders",
            path: "/admin/archived-orders",
            permission: "archived orders",
          },
        ],
      },
      Transactions: {
        order: 3,
        standalone: true,
        path: "/admin/transactions",
        icon: "CreditCardOutlined",
        permissions: ["transactions"],
      },
      Customers: {
        order: 4,
        standalone: true,
        path: "/admin/customers",
        icon: "UserOutlined",
        permissions: ["customers"],
      },
    },
  },
  MANAGEMENT: {
    order: 2,
    label: "MANAGEMENT",
    modules: {
      "Company Management": {
        order: 1,
        standalone: false,
        icon: "BankOutlined",
        permissions: [
          "company setup",
          "staff",
          "revenue",
          "order and operations",
        ],
        children: [
          {
            label: "Company Setup",
            path: "/admin/company-setup",
            permission: "company setup",
          },
          { label: "Staff", path: "/admin/staff", permission: "staff" },
          { label: "Revenue", path: "/admin/revenue", permission: "revenue" },
          {
            label: "Order and Operations",
            path: "/admin/operations",
            permission: "order and operations",
          },
        ],
      },
      "Product Management": {
        order: 2,
        standalone: false,
        icon: "AppstoreOutlined",
        permissions: [
          "category",
          "bulk purchase",
          "catering",
          "inventory control",
        ],
        children: [
          {
            label: "Category",
            path: "/admin/categories",
            permission: "category",
          },
          {
            label: "Bulk Purchase",
            path: "/admin/bulk-purchases",
            permission: "bulk purchase",
          },
          {
            label: "Catering",
            path: "/admin/catering",
            permission: "catering",
          },
          {
            label: "Inventory Control",
            path: "/admin/inventory-control",
            permission: "inventory control",
          },
        ],
      },
      "Customer Experience": {
        order: 3,
        standalone: false,
        icon: "UserOutlined",
        permissions: [
          "promos and discounts",
          "feedback and ratings",
          "terms and conditions",
        ],
        children: [
          {
            label: "Promos & Discounts",
            path: "/admin/promotions",
            permission: "promos and discounts",
          },
          {
            label: "Feedback & Ratings",
            path: "/admin/feedback",
            permission: "feedback and ratings",
          },
          {
            label: "Terms & Conditions",
            path: "/admin/terms&conditions",
            permission: "terms and conditions",
          },
        ],
      },
    },
  },
};

/**
 * Check if user has a specific permission
 */
export const hasPermission = (userPermissions, permissionName) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  const normalizedTarget = normalize(permissionName);
  return userPermissions.some((p) => normalize(p.name) === normalizedTarget);
};

/**
 * Check if user has any of the given permissions
 */
export const hasAnyPermission = (userPermissions, permissionNames) => {
  return permissionNames.some((name) => hasPermission(userPermissions, name));
};

/**
 * Get route path for a permission name
 */
export const getRouteForPermission = (permissionName) => {
  const normalized = normalize(permissionName);
  return PERMISSION_ROUTE_MAP[normalized] || null;
};

/**
 * Check if user can access a specific route
 */
export const canAccessRoute = (userPermissions, routePath) => {
  if (!userPermissions || !routePath) return false;

  // Find permission that matches this route
  const normalizedRoute = routePath.toLowerCase();

  for (const [permName, permRoute] of Object.entries(PERMISSION_ROUTE_MAP)) {
    if (permRoute.toLowerCase() === normalizedRoute) {
      return hasPermission(userPermissions, permName);
    }
  }

  // Default routes (like /admin index) - allow if user has any permission
  if (normalizedRoute === "/admin" || normalizedRoute === "/admin/") {
    return userPermissions.length > 0;
  }

  return false;
};

/**
 * Get all accessible routes for a user
 */
export const getAccessibleRoutes = (userPermissions) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return [];

  const routes = [];
  userPermissions.forEach((permission) => {
    const route = getRouteForPermission(permission.name);
    if (route) {
      if (!routes.includes(route)) {
        routes.push(route);
      }
    }
  });

  // Debug log
  // console.log('User permissions:', userPermissions.map(p => p.name));
  // console.log('Accessible routes:', routes);

  return routes;
};

/**
 * Get first accessible route (for default redirect)
 */
export const getDefaultRoute = (userPermissions) => {
  const routes = getAccessibleRoutes(userPermissions);

  // Priority: Dashboard > first available
  if (routes.includes("/admin/dashboard")) return "/admin/dashboard";
  return routes[0] || "/admin";
};
